import type {
  ImportCategoryBreakdownItem,
  ImportDaySection,
  ImportFileError,
  ImportMonthSection,
  ImportProgress,
  ImportWsTransaction,
} from '../../../types/import';

function dateKey(iso: string): string {
  return iso.slice(0, 10);
}

function monthKey(iso: string): string {
  return iso.slice(0, 7);
}

export function formatDayLabel(iso: string): string {
  const date = new Date(iso.includes('T') ? iso : `${iso}T12:00:00`);
  if (Number.isNaN(date.getTime())) return iso.slice(0, 10);
  return date.toLocaleDateString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

export function formatMonthLabel(yyyyMm: string): string {
  const date = new Date(`${yyyyMm}-01T12:00:00`);
  if (Number.isNaN(date.getTime())) return yyyyMm;
  return date.toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
}

export function sortTxNewestFirst(txs: ImportWsTransaction[]): ImportWsTransaction[] {
  return [...txs].sort((a, b) => {
    const byDate = dateKey(b.date).localeCompare(dateKey(a.date));
    if (byDate !== 0) return byDate;
    return b.amount - a.amount;
  });
}

/** Debit spend by category, highest first. Credits ignored for amount. */
export function deriveCategoryBreakdown(
  transactions: ImportWsTransaction[],
): ImportCategoryBreakdownItem[] {
  const map = new Map<string, ImportCategoryBreakdownItem>();

  for (const tx of transactions) {
    if (tx.tab !== 'spend') continue;
    const name = tx.categoryName || 'Other';
    const existing = map.get(name);
    const addAmount = tx.direction === 'debit' ? tx.amount : 0;
    if (existing) {
      existing.count += 1;
      existing.amount += addAmount;
    } else {
      map.set(name, { categoryName: name, amount: addAmount, count: 1 });
    }
  }

  return [...map.values()].sort((a, b) => b.amount - a.amount || b.count - a.count);
}

/** Newest month first; within month, newest day first. */
export function deriveMonthSections(transactions: ImportWsTransaction[]): ImportMonthSection[] {
  const sorted = sortTxNewestFirst(transactions);
  const byMonth = new Map<string, ImportWsTransaction[]>();

  for (const tx of sorted) {
    const key = monthKey(tx.date);
    const list = byMonth.get(key);
    if (list) list.push(tx);
    else byMonth.set(key, [tx]);
  }

  const monthKeys = [...byMonth.keys()].sort((a, b) => b.localeCompare(a));

  return monthKeys.map((key) => {
    const txs = byMonth.get(key) || [];
    const byDay = new Map<string, ImportWsTransaction[]>();
    for (const tx of txs) {
      const day = dateKey(tx.date);
      const list = byDay.get(day);
      if (list) list.push(tx);
      else byDay.set(day, [tx]);
    }

    const dayKeys = [...byDay.keys()].sort((a, b) => b.localeCompare(a));
    const days: ImportDaySection[] = dayKeys.map((day) => ({
      day,
      label: formatDayLabel(day),
      items: byDay.get(day) || [],
    }));

    const debitTotal = txs
      .filter((tx) => tx.direction === 'debit' && tx.tab === 'spend')
      .reduce((sum, tx) => sum + tx.amount, 0);

    return {
      monthKey: key,
      label: formatMonthLabel(key),
      count: txs.length,
      debitTotal,
      days,
    };
  });
}

/** Status after a parse event, e.g. "Added 12 txs from Jul 2026 · Dining building…" */
export function buildImageParsedStatusMessage(
  allTransactions: ImportWsTransaction[],
  runningTransactionCount: number,
  newlyParsed: ImportWsTransaction[],
): string {
  const source = newlyParsed.length > 0 ? newlyParsed : allTransactions;
  const months = [
    ...new Set(source.map((tx) => monthKey(tx.date)).filter((k) => k.length === 7)),
  ].sort((a, b) => b.localeCompare(a));
  const monthHint = months.length > 0 ? formatMonthLabel(months[0]) : null;

  const breakdown = deriveCategoryBreakdown(allTransactions);
  const topCategory = breakdown[0]?.categoryName;

  const countPart = `Added ${runningTransactionCount} tx${
    runningTransactionCount === 1 ? '' : 's'
  }`;
  const monthPart = monthHint ? ` from ${monthHint}` : '';
  const categoryPart = topCategory ? ` · ${topCategory} building…` : '…';

  return `${countPart}${monthPart}${categoryPart}`;
}

/** Newest month label for chips / status hints. */
export function newestMonthHint(transactions: ImportWsTransaction[]): string | null {
  if (!transactions.length) return null;
  const keys = [
    ...new Set(transactions.map((tx) => monthKey(tx.date)).filter((k) => k.length === 7)),
  ].sort((a, b) => b.localeCompare(a));
  return keys.length > 0 ? formatMonthLabel(keys[0]) : null;
}

export function formatPartialImportStatus(
  processedImages: number,
  totalImages: number,
): string {
  const failed = Math.max(0, totalImages - processedImages);
  if (failed <= 0) return '';
  const noun = failed === 1 ? 'statement' : 'statements';
  const reupload = failed === 1 ? 're-upload that PDF' : 're-upload those PDFs';
  return `${processedImages}/${totalImages} analyzed · ${failed} ${noun} failed — ${reupload}`;
}

export function isPartialImport(progress: Pick<
  ImportProgress,
  'processedImages' | 'totalImages' | 'fileErrors' | 'phase'
>): boolean {
  if ((progress.fileErrors?.length ?? 0) > 0) return true;
  if (progress.phase !== 'complete') return false;
  return progress.totalImages > 0 && progress.processedImages < progress.totalImages;
}

export function upsertFileError(
  existing: ImportFileError[],
  next: ImportFileError,
): ImportFileError[] {
  const already = existing.some(
    (entry) => entry.imageIndex === next.imageIndex && entry.s3Key === next.s3Key,
  );
  if (already) return existing;
  return [...existing, next].sort((a, b) => a.imageIndex - b.imageIndex);
}

export function formatFileFailedMessage(error: ImportFileError): string {
  return `File ${error.imageIndex + 1} failed — ${error.error}`;
}
